import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
} from './92-sky-flight-frame.mjs';
import { prepareSkyLeviathanMesh, skyLeviathanMeshSweep } from './96-sky-leviathan-contacts.mjs';

const frozen = (value) => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(frozen);
    Object.freeze(value);
  }
  return value;
};
/** Provisional blockout metres, not final art dimensions or campaign policy.
 * Local forward is -Z. Water is not a supporting surface. */
export const SKY_CATHEDRAL_SPACE = frozen({
  root: { x: 0, y: 4, z: 0 },
  waterY: 2,
  pilotRadius: 1.27,
  naveFloor: 14,
  naveCeiling: 24,
  naveHalfWidth: 5,
  naveHalfLength: 11,
  underpassHalfWidth: 3,
  underpassHeight: 6,
  foundationY: 4,
  rearPivot: { x: 0, y: 0, z: 14 },
  maximumKneel: (20 * Math.PI) / 180,
  legLength: 8.5,
  footHalfWidth: 1.5,
  footHalfLength: 2,
  legIds: ['front-left', 'front-right', 'rear-left', 'rear-right', 'middle-left', 'middle-right'],
});
/** Rigid frame with radians about local X. No scale is embedded. */
export function skyCathedralFrame(origin, angle = 0) {
  const c = Math.cos(angle),
    s = Math.sin(angle);
  return {
    origin: { ...origin },
    right: { x: 1, y: 0, z: 0 },
    up: { x: 0, y: c, z: s },
    back: { x: 0, y: -s, z: c },
  };
}
/** Immutable watertight blockout box; half dimensions and vertices are metres.
 * Degenerate/nonfinite dimensions throw at authoring, never generate fake walls. */
export function skyCathedralBox(half) {
  if (![half.x, half.y, half.z].every((v) => Number.isFinite(v) && v > 0))
    throw new TypeError('Cathedral box needs positive metre half extents');
  const points = [];
  for (const z of [-1, 1])
    for (const y of [-1, 1])
      for (const x of [-1, 1]) points.push({ x: x * half.x, y: y * half.y, z: z * half.z });
  const faces = [
    [0, 2, 3, 1],
    [4, 5, 7, 6],
    [0, 1, 5, 4],
    [2, 6, 7, 3],
    [0, 4, 6, 2],
    [1, 3, 7, 5],
  ];
  const triangles = frozen(
    faces.flatMap(([a, b, c, d], index) => [
      { id: `${index}-a`, a: points[a], b: points[b], c: points[c] },
      { id: `${index}-b`, a: points[a], b: points[c], c: points[d] },
    ]),
  );
  prepareSkyLeviathanMesh(triangles);
  return triangles;
}
const geometryCache = new Map();
/** Reuse immutable box geometry across poses. A bounded authored set of sizes
 * is expected; articulated variable-length struts use unit boxes instead. */
export function skyCathedralSolid(id, origin, half, kind = 'stone', frame = null) {
  const key = `${half.x}/${half.y}/${half.z}`;
  if (!geometryCache.has(key)) {
    // Eviction drops only this reuse reference. Live poses keep their immutable
    // geometry and BVH; repeated authoring/indefinite encounters cannot retain
    // an unbounded history of floating-point box dimensions here.
    if (geometryCache.size >= 256) geometryCache.delete(geometryCache.keys().next().value);
    geometryCache.set(key, skyCathedralBox(half));
  }
  return {
    id,
    kind,
    frame: frame ?? skyCathedralFrame(origin),
    triangles: geometryCache.get(key),
    halfExtents: { ...half },
    collisionEnabled: true,
  };
}
const city = [
  skyCathedralSolid('foundation-court', { x: 0, y: 2, z: 0 }, { x: 24, y: 2, z: 42 }, 'foundation'),
];
for (const side of [-1, 1]) {
  city.push(
    skyCathedralSolid(
      `roof-${side}-approach`,
      { x: side * 39, y: 4, z: -8 },
      { x: 9, y: 4, z: 27 },
      'roof',
    ),
  );
  city.push(
    skyCathedralSolid(
      `roof-${side}-rear`,
      { x: side * 39, y: 6, z: 47 },
      { x: 9, y: 6, z: 12 },
      'roof',
    ),
  );
  city.push(
    skyCathedralSolid(
      `bridge-${side}`,
      { x: side * 22, y: 2, z: -30 },
      { x: 8, y: 2, z: 6 },
      'foundation',
    ),
  );
}
/** Finite actual city solids. Roofs remain collidable from below and sides. */
export const SKY_CATHEDRAL_CITY = frozen(city);
/** The production court is rendered from these exact triangles and original
 * stone artwork. Historical blockout rooftops are absent from the real arena. */
export const SKY_CATHEDRAL_COURT = frozen([city[0]]);
export const skyCathedralCity = (pose) =>
  pose?.artwork ? SKY_CATHEDRAL_COURT : SKY_CATHEDRAL_CITY;
/** Highest authored horizontal support at X/Z, or null over open floodwater.
 * The identity accompanies height so planted soles cannot silently use water. */
export function skyCathedralSupport(x, z) {
  if (![x, z].every(Number.isFinite)) return null;
  let best = null;
  for (const part of SKY_CATHEDRAL_CITY) {
    const o = part.frame.origin,
      h = part.halfExtents;
    if (Math.abs(x - o.x) <= h.x && Math.abs(z - o.z) <= h.z) {
      const y = o.y + h.y;
      if (!best || y > best.y) best = { id: part.id, y };
    }
  }
  return best;
}
const finitePoint = (p) => p && [p.x, p.y, p.z].every(Number.isFinite);
// Private transforms consume a frame validated once for this contact query.
// Revalidating/cloning four basis vectors for every bound corner dominated
// the six-legged rigid assembly's fixed-step work. Mutable frames are never cached.
function localPoint(f, p) {
  const x = p.x - f.origin.x,
    y = p.y - f.origin.y,
    z = p.z - f.origin.z;
  return {
    x: x * f.right.x + y * f.right.y + z * f.right.z,
    y: x * f.up.x + y * f.up.y + z * f.up.z,
    z: x * f.back.x + y * f.back.y + z * f.back.z,
  };
}
function worldPoint(f, x, y, z) {
  return {
    x: f.origin.x + (f.right.x * x + f.up.x * y + f.back.x * z),
    y: f.origin.y + (f.right.y * x + f.up.y * y + f.back.y * z),
    z: f.origin.z + (f.right.z * x + f.up.z * y + f.back.z * z),
  };
}

/** Nearest exact static solid surface swept by a sphere/shot. No target proxy
 * substitutes for walls; city and all posed solid triangles participate. */
export function skyCathedralSweep(from, to, pose, radius = SKY_CATHEDRAL_SPACE.pilotRadius) {
  if (
    !pose?.parts ||
    !Number.isFinite(radius) ||
    radius < 0 ||
    !finitePoint(from) ||
    !finitePoint(to)
  )
    return null;
  let first = null;
  for (const part of [...skyCathedralCity(pose), ...pose.parts]) {
    if (part.collisionEnabled === false) continue;
    const frame = skyFlightFrame(part.frame);
    if (!frame) return null;
    const a = localPoint(frame, from),
      b = localPoint(frame, to);
    if (
      radius > 0 &&
      !part.authoredSurface &&
      part.halfExtents &&
      ['x', 'y', 'z'].every((key) => Math.abs(a[key]) < part.halfExtents[key])
    )
      return { fraction: 0, point: { ...from }, partId: part.id, triangleId: null };
    const hit = skyLeviathanMeshSweep(a, b, part.triangles, radius);
    if (hit && (!first || hit.fraction < first.fraction))
      first = {
        ...hit,
        partId: part.id,
        point: worldPoint(frame, hit.point.x, hit.point.y, hit.point.z),
      };
  }
  return first;
}
/** Check the complete sphere crossing a rectangular aperture, including the
 * earliest and latest plane contact. A diagonal edge scrape is not a crossing. */
export function skyCathedralPortalCrossing(
  from,
  to,
  portal,
  radius = SKY_CATHEDRAL_SPACE.pilotRadius,
) {
  if (!portal || ![from, to].every((p) => p && [p.x, p.y, p.z].every(Number.isFinite)))
    return false;
  const dot = (p, axis) =>
    ['x', 'y', 'z'].reduce((sum, k) => sum + (p[k] - portal.center[k]) * axis[k], 0);
  const a = dot(from, portal.normal),
    b = dot(to, portal.normal);
  if (!(a <= -radius && b >= radius)) return false;
  for (const distance of [-radius, radius]) {
    const t = (distance - a) / (b - a);
    const p = Object.fromEntries(['x', 'y', 'z'].map((k) => [k, from[k] + (to[k] - from[k]) * t]));
    if (
      Math.abs(dot(p, portal.right)) + radius > portal.halfWidth ||
      Math.abs(dot(p, portal.up)) + radius > portal.halfHeight
    )
      return false;
  }
  return true;
}

/** Conservative contact against a changing rigid blockout pose. The caller
 * supplies consecutive bounded simulation steps; current surfaces are expanded
 * by twice each part's greatest endpoint vertex movement. Static city surfaces
 * keep the exact pilot radius, avoiding a distant tower's motion turning the
 * entire foundation into an inflated obstacle. Changed topology/dimensions fail
 * explicitly: an unvalidated replacement cannot silently become passable.
 * For long collapse motions use100's bounded angular substeps as well. */
export function skyCathedralMovingSweep(
  from,
  to,
  previousPose,
  nextPose,
  radius = SKY_CATHEDRAL_SPACE.pilotRadius,
) {
  if (!previousPose?.valid || !nextPose?.valid || !Number.isFinite(radius) || radius < 0)
    return { invalid: true, reason: 'invalid-moving-pose' };
  if (!finitePoint(from) || !finitePoint(to))
    return { invalid: true, reason: 'invalid-moving-point' };
  const previous = new Map(previousPose.parts.map((part) => [part.id, part]));
  if (previous.size !== nextPose.parts.length)
    return { invalid: true, reason: 'changed-moving-topology' };
  let first = skyCathedralSweep(from, to, { parts: [], artwork: nextPose.artwork }, radius);
  for (const part of nextPose.parts) {
    const old = previous.get(part.id),
      h = part.halfExtents;
    if (!old || ['x', 'y', 'z'].some((key) => Math.abs(old.halfExtents[key] - h[key]) > 1e-7))
      return { invalid: true, reason: 'changed-moving-geometry' };
    const oldFrame = skyFlightFrame(old.frame),
      frame = skyFlightFrame(part.frame);
    if (!oldFrame || !frame) return { invalid: true, reason: 'invalid-moving-frame' };
    let displacement = 0;
    for (const x of [-h.x, h.x])
      for (const y of [-h.y, h.y])
        for (const z of [-h.z, h.z]) {
          const a = worldPoint(oldFrame, x, y, z);
          const b = worldPoint(frame, x, y, z);
          displacement = Math.max(displacement, Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z));
        }
    const a = localPoint(frame, from),
      b = localPoint(frame, to);
    const margin = displacement * 2;
    const inside =
      !part.authoredSurface &&
      radius > 0 &&
      ['x', 'y', 'z'].every((key) => Math.abs(a[key]) < h[key]);
    const hit = inside
      ? { fraction: 0, point: a, triangleId: null }
      : skyLeviathanMeshSweep(a, b, part.triangles, radius + margin);
    if (hit && (!first || hit.fraction < first.fraction))
      first = {
        ...hit,
        point: worldPoint(frame, hit.point.x, hit.point.y, hit.point.z),
        partId: part.id,
        motionMargin: margin,
      };
  }
  return first;
}

/** Bounded exact box/sphere depenetration after a moving wall reaches a pilot.
 * Returns a proposed physical position, never mutates input. The controller
 * must reject/hold unsafe motion when blocked; no teleport beyond maxDistance
 * is permitted. Eight passes resolve ordinary corners; impossible wedges fail
 * explicitly. This applies only to the shared authored box blockout. */
export function skyCathedralResolvePilot(
  point,
  pose,
  radius = SKY_CATHEDRAL_SPACE.pilotRadius,
  maxDistance = 0.75,
) {
  const invalid = { point, distance: 0, partIds: [], blocked: true };
  if (
    !point ||
    !['x', 'y', 'z'].every((key) => Number.isFinite(point[key])) ||
    !pose?.valid ||
    ![radius, maxDistance].every((value) => Number.isFinite(value) && value > 0)
  )
    return invalid;
  // Exact textured surfaces cannot use box depenetration: the shell's bounds
  // contain its navigable nave. Hold the mechanism rather than push a pilot
  // through unverified mesh topology; manual movement can clear the contact.
  if (pose.artwork) {
    const hit = skyCathedralSweep(point, point, pose, radius);
    return hit
      ? { ...invalid, partIds: [hit.partId] }
      : { point: { ...point }, distance: 0, partIds: [], blocked: false };
  }
  let result = { ...point };
  const partIds = new Set();
  for (let iteration = 0; iteration < 8; iteration++) {
    let moved = false;
    for (const part of [...skyCathedralCity(pose), ...pose.parts]) {
      if (part.collisionEnabled === false) continue;
      const local = skyFlightPointToLocal(part.frame, result),
        h = part.halfExtents;
      const nearest = Object.fromEntries(
        ['x', 'y', 'z'].map((key) => [key, Math.max(-h[key], Math.min(h[key], local[key]))]),
      );
      const delta = { x: local.x - nearest.x, y: local.y - nearest.y, z: local.z - nearest.z };
      const distance = Math.hypot(delta.x, delta.y, delta.z);
      if (distance >= radius - 1e-8) continue;
      if (distance > 1e-10) {
        for (const key of ['x', 'y', 'z'])
          local[key] = nearest[key] + (delta[key] / distance) * (radius + 1e-6);
      } else {
        const axis = ['x', 'y', 'z'].sort(
          (a, b) => h[a] - Math.abs(local[a]) - (h[b] - Math.abs(local[b])),
        )[0];
        local[axis] = (local[axis] < 0 ? -1 : 1) * (h[axis] + radius + 1e-6);
      }
      const candidate = skyFlightPointToWorld(part.frame, local);
      const total = Math.hypot(candidate.x - point.x, candidate.y - point.y, candidate.z - point.z);
      if (total > maxDistance)
        return { point: { ...point }, distance: 0, partIds: [...partIds, part.id], blocked: true };
      result = candidate;
      partIds.add(part.id);
      moved = true;
    }
    if (!moved)
      return {
        point: result,
        distance: Math.hypot(result.x - point.x, result.y - point.y, result.z - point.z),
        partIds: [...partIds],
        blocked: false,
      };
  }
  return { point: { ...point }, distance: 0, partIds: [...partIds], blocked: true };
}
